import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Tooltip,
} from "@mui/material";
import { useFormikContext } from "formik";
import ExpansionHeader from "../../../components/AccordionHeader";
import {
  AlarmCenterIcon,
  ExpandIcon,
  WarningIcon,
  WhiteLabelIcon,
} from "../../../utils/icons";
import { Colors } from "../../../utils/style/colors";
import Renderer from "./Renderer";
import Switch from "./Switch";
import VisibilityToggle from "./VisibilityToggle";
import { useMemo } from "react";
import { fieldKey, sortFields } from "../utils";
import { useTranslation } from "react-i18next";

export default function Group({
  headerText,
  settings,
  onChange,
  groupName,
  changedSettings,
  changeInfo,
  hasError,
  icon = WhiteLabelIcon,
  depth = 0,
  style = {},
  headerStyle,
  headerField,
  // Account-wide visibility policy; absent (admin device page) hides nothing.
  visibility,
  // ARC-managed section: read-only.
  locked = false,
}) {
  const { t } = useTranslation();
  // Count of unsynced fields under this group (header marker + default-open).
  const changeCount = useMemo(() => {
    if (!changedSettings || changedSettings.size === 0) return 0;
    const prefix = `${groupName}.`;
    let count = 0;
    for (const id of changedSettings) {
      if (id.startsWith(prefix)) count += 1;
    }
    return count;
  }, [changedSettings, groupName]);
  const hasChanges = changeCount > 0;
  // Order by backend schema.position; only pin the section's on/off toggle
  // (e.g. manDownEnabled) to the top when it has NO position.
  const toggleKey = `${(groupName || "").split(".").pop()}Enabled`;
  const fields = useMemo(
    () =>
      Object.entries(settings)
        .filter(([key]) => !["type", "name"].includes(key))
        .sort(([aKey, aVal], [bKey, bVal]) => {
          const aTop = aKey === toggleKey && aVal.schema?.position == null;
          const bTop = bKey === toggleKey && bVal.schema?.position == null;
          if (aTop !== bTop) return aTop ? -1 : 1;
          return sortFields(aVal.schema, bVal.schema);
        }),
    [settings, toggleKey],
  );
  // Drop always-hidden fields and whatever the account hides; everything else
  // is shown (`schema.advanced` no longer gates anything).
  const visibleFields = useMemo(
    () =>
      fields.filter(([key, field]) => {
        if (visibility?.enforced) {
          const childId = `${groupName}.${key}`;
          const childHidden =
            field.type === "GROUP"
              ? visibility.isGroupHidden(childId)
              : visibility.isSettingHidden(childId);
          if (childHidden) return false;
        }
        if (field.type === "GROUP") return true;
        if (field.schema?.hideOn && field.schema.hideOn[""] === "") return false;
        return true;
      }),
    [fields, groupName, visibility],
  );
  const isHidden = visibleFields.length === 0;
  const groupHidden = !!visibility?.isGroupHidden?.(groupName);
  // The section's on/off switch sits in the header, not in `visibleFields`, so
  // it gets its own eye: a feature can stay tunable but not switchable off.
  const headerFieldId = headerField
    ? `${groupName}.${fieldKey(headerField.name)}`
    : null;
  const headerFieldHidden =
    !!headerFieldId && !!visibility?.isSettingHidden?.(headerFieldId);
  const hideHeaderField = !!visibility?.enforced && headerFieldHidden;
  // While editing, dim what the customer will not get to see.
  const dimmed = visibility?.editable && groupHidden ? 0.55 : 1;
  const headerDimmed = visibility?.editable && headerFieldHidden ? 0.55 : 1;

  const formik = useFormikContext();
  const headerFieldData = useMemo(() => {
    if (!headerField) return undefined;

    return formik.getFieldMeta(headerFieldId)?.value;
  }, [formik, headerField, headerFieldId]);

  if (isHidden) {
    return null;
  }

  // Hidden for the account: the whole section is gone, never just collapsed.
  if (visibility?.enforced && groupHidden) {
    return null;
  }

  // Switch hidden and the feature off: nothing to see, and these groups have no
  // expand icon, so drop the header rather than leave a dead row.
  if (hideHeaderField && !headerFieldData) {
    return null;
  }

  return (
    <Accordion
      style={{
        borderRadius: depth === 0 ? "0 !important" : undefined,
        marginLeft: depth > 0 ? 16 : 0,
        ...style,
      }}
      // Non-toggle groups default open when they hold unsaved changes.
      {...(headerField
        ? { expanded: headerFieldData }
        : { defaultExpanded: hasChanges })}
    >
      <AccordionSummary
        style={{
          background:
            depth === 0
              ? Colors[hasError ? "lightRed" : "gray10"]
              : Colors.gray5,
        }}
        expandIcon={headerField ? null : <ExpandIcon />}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            flex: 1,
            minWidth: 0,
            opacity: dimmed,
          }}
        >
          {hasChanges && (
            <Tooltip
              arrow
              placement="top"
              title={t("nSettingsNotSynced", {
                count: changeCount,
                defaultValue: "{count} setting(s) not yet synced to the device",
              })}
            >
              <Box
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.25,
                  mr: 1,
                  color: Colors.orange,
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  cursor: "help",
                }}
              >
                <WarningIcon size={0.62} color={Colors.orange} />
                {changeCount}
              </Box>
            </Tooltip>
          )}
          {headerField && !hideHeaderField ? (
            <>
              <Switch
                id={headerFieldId}
                name={headerField.name}
                disabled={locked}
                boxSx={{
                  p: 0,
                  flex: 1,
                  justifyContent: "space-between",
                  opacity: headerDimmed,
                }}
                onChange={(key, newValue) =>
                  onChange(headerField.name, newValue)
                }
              />
              {visibility?.editable && (
                <VisibilityToggle
                  hidden={headerFieldHidden}
                  onToggle={() => visibility.toggleSetting(headerFieldId)}
                  sx={{ ml: 0.5 }}
                />
              )}
            </>
          ) : (
            <ExpansionHeader
              icon={icon}
              text={t(headerText)}
              headerStyle={headerStyle}
            />
          )}
        </Box>
        {visibility?.editable && (
          <VisibilityToggle
            group
            hidden={groupHidden}
            onToggle={() => visibility.toggleGroup(groupName)}
          />
        )}
      </AccordionSummary>
      <AccordionDetails style={{ padding: 0, opacity: dimmed }}>
        {locked && depth === 0 && (
          <Alert
            severity="info"
            icon={<AlarmCenterIcon color="inherit" size={0.9} />}
            sx={{ borderRadius: 0 }}
          >
            <AlertTitle>{t("notConfigurableForThisDevice")}</AlertTitle>
            {t("disabledBecauseOfAlarmCenter")}
          </Alert>
        )}
        {visibleFields.map(([fieldName, field]) => {
          // Check if this is a nested group
          if (field.type === "GROUP") {
            const { headerField, ...fieldSettings } = field;
            return (
              <Group
                key={fieldName}
                headerText={fieldSettings.name || fieldName}
                settings={fieldSettings}
                changedSettings={changedSettings}
                changeInfo={changeInfo}
                onChange={onChange}
                groupName={`${groupName}.${fieldName}`}
                hasError={false}
                depth={depth + 1}
                icon={null}
                style={{ margin: 8 }}
                headerStyle={{ marginLeft: 0 }}
                headerField={headerField}
                visibility={visibility}
                locked={locked}
              />
            );
          }

          // Regular field
          return (
            <Renderer
              id={`${groupName}.${fieldKey(fieldName)}`}
              key={fieldName}
              name={fieldName}
              value={field.reportedState}
              schema={field.schema}
              // Inputs report their field id; write under the setting's name.
              onChange={(key, value) =>
                onChange(typeof key === "string" ? fieldName : key, value)
              }
              groupName={groupName}
              changedSettings={changedSettings}
              changeInfo={changeInfo}
              visibility={visibility}
              locked={locked}
            />
          );
        })}
      </AccordionDetails>
    </Accordion>
  );
}
